import { useMutation } from "@faire/web-api--source/endpoints/www/api/email/resubscribe/post-hooks";
import emailPreferencesPath from "@faire/web-api--source/routes/www/retailer/settings/email-preferences";
import { Button } from "@faire/web/slate-deprecated/Button";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { Anchor } from "@faire/web/ui/LinkAnchor";
import React, { useMemo, useState } from "react";

import { getUser } from "../../../serialized-data/getUser";

interface ResubscribeNudgeInputProps {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const ResubscribeNudgeInput: React.FC<ResubscribeNudgeInputProps> = ({
  onSuccess,
  onError,
}) => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const user = getUser();
  const { mutate: resubscribe, isPending } = useMutation({
    onSuccess: () => {
      setIsSubscribed(true);
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error as Error);
    },
  });

  const handleResubscribe = () => {
    if (!user?.email_address) {
      onError?.(new Error("No email address found"));
      return;
    }

    resubscribe([{ email_address: user.email_address }]);
  };

  if (isSubscribed) {
    return (
      <div>
        <Typography
          variant={{
            mobileAndAbove: "pageHeaderSerifRegular",
            desktopAndAbove: "displaySSerifRegular",
            xLargeAndAbove: "displayMSerifRegular",
          }}
        >
          <StrictLocalMsg
            id="bsOjvW"
            defaultMessage="You're in!"
            description={{
              text: "Success message confirming user has successfully resubscribed to marketing emails",
              img: "https://cdn.faire.com/fastly/497e51e3ac88c71b79d71224ba26cef0a3626de76fc5b9ae604a019d0fd333b8.png",
            }}
          />
        </Typography>
        <Spacer size="3x" />
        <Typography
          variant={{
            mobileAndAbove: "paragraphSansRegular",
            desktopAndAbove: "paragraphSansRegular",
          }}
        >
          <StrictLocalMsg
            id="95OLy2"
            defaultMessage="{emailAddress} is now subscribed to Faire's marketing emails. You can opt-out at any time on your account settings page <LinkFormatting>here</LinkFormatting>."
            description={{
              text: "Confirmation message shown after successful resubscription with link to email preferences",
              img: "https://cdn.faire.com/fastly/497e51e3ac88c71b79d71224ba26cef0a3626de76fc5b9ae604a019d0fd333b8.png",
            }}
            values={{
              emailAddress: user?.email_address || "",
              LinkFormatting: (chunk) => (
                <Anchor href={emailPreferencesPath()} className="underline">
                  {chunk}
                </Anchor>
              ),
            }}
          />
        </Typography>
      </div>
    );
  }

  return (
    <div>
      <Button
        onClick={handleResubscribe}
        variant="secondary"
        className="!w-fit"
        loading={isPending}
        disabled={isPending}
        data-testid="resubscribe-button"
      >
        <StrictLocalMsg
          id="9SeM+2"
          defaultMessage="Subscribe to emails"
          description={{
            text: "Button text for users to subscribe to marketing emails",
            img: "https://cdn.faire.com/fastly/adcf0f415638c0c1b4ebca158b70e66b21eed428697978830eb840d217644fb2.png",
          }}
        />
      </Button>
    </div>
  );
};

interface ResubscribeNudgeInputWithFooterProps {
  variant: "A" | "B" | "C";
}

// Wrapper component for footer integration
export const ResubscribeNudgeInputWithFooter: React.FC<
  ResubscribeNudgeInputWithFooterProps
> = ({ variant }) => {
  const [isSubscribed, setIsSubscribed] = useState(false);

  const message = useMemo(() => {
    switch (variant) {
      case "A":
        return (
          <StrictLocalMsg
            id="qHi7AO"
            defaultMessage="Stay connected with updates on new products, news, and events."
            description={{
              text: "Encouraging header text for resubscribe banner variant A",
              img: "https://cdn.faire.com/fastly/c56c90e63317ecf6c9b6410d33406370621a8db99bfd7ed65638666133833df9.png",
            }}
          />
        );
      case "B":
        return (
          <StrictLocalMsg
            id="dV0oSx"
            defaultMessage="Be first to know about important updates and new arrivals."
            description={{
              text: "Encouraging header text for resubscribe banner variant B",
              img: "https://cdn.faire.com/fastly/10af8cd8c5557e5e05c8143a88edd6e62e04b620eefb5d12b2cb1aaa59672706.png",
            }}
          />
        );
      case "C":
        return (
          <StrictLocalMsg
            id="5rElwo"
            defaultMessage="Don't miss out. Get the latest on trending products and new arrivals your customers will love."
            description={{
              text: "Encouraging header text for resubscribe banner variant C",
              img: "https://cdn.faire.com/fastly/b11ac7e90c123edcc70493150d50a0da49d207120637772a16b7bbf0cf5fa5df.png",
            }}
          />
        );
    }
  }, [variant]);

  return (
    <div>
      {!isSubscribed && (
        <>
          <Typography
            variant={{
              mobileAndAbove: "pageHeaderSerifRegular",
              desktopAndAbove: "displaySSerifRegular",
              xLargeAndAbove: "displaySSerifRegular",
            }}
          >
            {message}
          </Typography>
          <Spacer size="3x" />
        </>
      )}
      <ResubscribeNudgeInput onSuccess={() => setIsSubscribed(true)} />
    </div>
  );
};
